import {create} from 'zustand';
import {StoryScreenModel} from '../../../entities';

interface StoryScreensState {
  screensByStory: Record<string, StoryScreenModel[]>;
  originalScreensByStory: Record<string, StoryScreenModel[]>; // Оригинальные данные с сервера
  getScreens: (storyId: string) => StoryScreenModel[];
  addScreen: (storyId: string, screen: Omit<StoryScreenModel, 'id'>) => void;
  updateScreen: (storyId: string, screenId: string, screen: Partial<StoryScreenModel>) => void;
  updateScreenOrder: (storyId: string, screens: StoryScreenModel[]) => void;
  commitScreens: (storyId: string, savedScreens: StoryScreenModel[]) => void;
  duplicateScreen: (storyId: string, screenId: string) => void;
  deleteScreen: (storyId: string, screenId: string) => void;
  resetScreens: (storyId: string) => void;
  setScreens: (storyId: string, screens: StoryScreenModel[]) => void;
  syncScreensFromServer: (storyId: string, screens: StoryScreenModel[]) => void;
  clearStory: (storyId: string) => void;
  hasChanges: (storyId: string) => boolean;
}

// Идентификатор экрана существует только на клиенте: сервер экраны не нумерует,
// он хранит и возвращает упорядоченный список. Поэтому id живёт ровно столько,
// сколько открыта страница, и нужен только как ключ сортировки для dnd-kit.
const generateId = () =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2, 11);

// Раньше стор persist-ился в localStorage, из-за чего несохранённый черновик экранов
// переживал перезагрузку и накладывался на изменившиеся серверные данные. Persist убран,
// но у тех, кто уже словил это, ключ остался — чистим один раз при загрузке модуля.
try {
  localStorage.removeItem('story-screens-storage');
} catch {
  // localStorage может быть недоступен (приватный режим, отключённые куки) — не мешаем загрузке
}

const normalizeForCompare = (screens: StoryScreenModel[]) => {
  if (!screens) return [];

  return screens.map((screen) => ({
    paragraphs: screen.paragraphs,

    button: screen.button
      ? {
          text: screen.button.text || null,
          link: screen.button.link || null,
          theme: screen.button.theme || null,
        }
      : null,

    theme: screen.theme || 'light',
    alignToBottom: !!screen.alignToBottom,

    mediaType: screen.mediaType || screen.media?.type || null,
    imageUploadId: screen.imageUploadId || null,
    videoUploadId: screen.videoUploadId || null,
    mediaFileName: screen.media
      ? screen.media.type === 'image'
        ? screen.media.image.fileName
        : screen.media.video.fileName
      : null,
    thumbnailFileName: screen.media?.type === 'video' ? screen.media.thumbnail?.fileName || null : null,
  }));
};

// Сравнение по содержимому: id в него не входит, поэтому наборы экранов сравнимы
// между загрузками, хотя идентификаторы у них каждый раз новые.
const screensDiffer = (left: StoryScreenModel[], right: StoryScreenModel[]) => {
  if (left.length !== right.length) return true;

  return JSON.stringify(normalizeForCompare(left)) !== JSON.stringify(normalizeForCompare(right));
};

// Приводит экраны с сервера к виду, в котором их ждёт UI: проставляет id и раскладывает
// media по полям превью.
const withMediaPreviews = (screens: StoryScreenModel[]): StoryScreenModel[] =>
  screens.map((screen) => {
    let imagePreview = screen.imagePreview || null;
    let videoPreview = screen.videoPreview || null;
    let thumbnailPreview = screen.thumbnailPreview || null;
    let mediaType = screen.mediaType || undefined;

    if (screen.media) {
      mediaType = screen.media.type;
      if (screen.media.type === 'image') {
        // Превью карточки — уменьшенная копия (story@webp); у файлов без неё — базовое изображение
        imagePreview = screen.media.image.thumbnailUrl || screen.media.image.url || imagePreview;
      } else if (screen.media.type === 'video') {
        videoPreview = screen.media.video.url || videoPreview;
        thumbnailPreview = screen.media.thumbnail?.url || thumbnailPreview;
      }
    }

    return {
      ...screen,
      id: screen.id || generateId(),
      mediaType,
      imagePreview,
      videoPreview,
      thumbnailPreview,
      // Серверные экраны одноразовых токенов загрузки не содержат
      imageUploadId: screen.imageUploadId || null,
      videoUploadId: screen.videoUploadId || null,
      button: screen.button && (screen.button.text || screen.button.link) ? screen.button : null,
    };
  });

export const useStoryScreensStore = create<StoryScreensState>()((set, get) => ({
  screensByStory: {},
  originalScreensByStory: {},

  getScreens: (storyId: string) => {
    return get().screensByStory[storyId] || [];
  },

  addScreen: (storyId: string, screen) => {
    const newScreen: StoryScreenModel = {
      ...screen,
      id: generateId(),
    };
    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: [...(state.screensByStory[storyId] || []), newScreen],
      },
    }));
  },

  updateScreen: (storyId: string, screenId: string, updatedScreen) => {
    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: (state.screensByStory[storyId] || []).map((screen) =>
          screen.id === screenId ? {...screen, ...updatedScreen} : screen,
        ),
      },
    }));
  },

  updateScreenOrder: (storyId: string, screens) => {
    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: screens,
      },
    }));
  },

  commitScreens: (storyId: string, savedScreens) => {
    // Вызывается после успешного сохранения экранов на сервер.
    // uploadId — одноразовый токен загрузки: после обработки на сервере он удаляется
    // (Redis + S3), и повторная его отправка приведёт к 422 "Upload not found".
    // Поэтому потраченные токены обнуляем — теперь идентичностью медиа экрана
    // является серверный media.fileName (придёт с рефетчем).
    //
    // Оригиналом становится именно отправленный на сервер снимок, а не текущее
    // состояние стора: иначе правки, сделанные пока запрос был в полёте, молча
    // пометились бы сохранёнными и были бы затёрты следующим рефетчем.
    const spentUploadIds = new Set(
      savedScreens.flatMap((screen) => [screen.imageUploadId, screen.videoUploadId]).filter(Boolean),
    );

    const clearSpentUploads = (screen: StoryScreenModel): StoryScreenModel => ({
      ...screen,
      imageUploadId: screen.imageUploadId && spentUploadIds.has(screen.imageUploadId) ? null : screen.imageUploadId,
      videoUploadId: screen.videoUploadId && spentUploadIds.has(screen.videoUploadId) ? null : screen.videoUploadId,
    });

    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: (state.screensByStory[storyId] || []).map(clearSpentUploads),
      },
      originalScreensByStory: {
        ...state.originalScreensByStory,
        [storyId]: savedScreens.map(clearSpentUploads),
      },
    }));
  },

  duplicateScreen: (storyId: string, screenId: string) => {
    const screens = get().screensByStory[storyId] || [];
    const screenToDuplicate = screens.find((s) => s.id === screenId);

    if (screenToDuplicate) {
      const duplicatedScreen: StoryScreenModel = {
        ...screenToDuplicate,
        id: generateId(),
      };

      set((state) => ({
        screensByStory: {
          ...state.screensByStory,
          [storyId]: [...(state.screensByStory[storyId] || []), duplicatedScreen],
        },
      }));
    }
  },

  deleteScreen: (storyId: string, screenId: string) => {
    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: (state.screensByStory[storyId] || []).filter((screen) => screen.id !== screenId),
      },
    }));
  },

  resetScreens: (storyId: string) => {
    const originalScreens = get().originalScreensByStory[storyId];

    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: originalScreens ? [...originalScreens] : [],
      },
    }));
  },

  setScreens: (storyId: string, screens) => {
    const screensWithIds = withMediaPreviews(screens);

    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: screensWithIds,
      },
      // Сохраняем оригинальные данные для сброса
      originalScreensByStory: {
        ...state.originalScreensByStory,
        [storyId]: screensWithIds,
      },
    }));
  },

  syncScreensFromServer: (storyId: string, screens) => {
    const serverScreens = withMediaPreviews(screens);
    const current = get().screensByStory[storyId] || [];
    const original = get().originalScreensByStory[storyId] || [];

    // Есть несохранённые правки — серверные данные идут только в «оригинал».
    // Рефетч приходит и сам по себе (возврат фокуса в окно, инвалидация после
    // соседней мутации), затирать им работу пользователя нельзя.
    if (screensDiffer(current, original)) {
      set((state) => ({
        originalScreensByStory: {
          ...state.originalScreensByStory,
          [storyId]: serverScreens,
        },
      }));
      return;
    }

    // Изменений нет и сервер вернул то же самое — не трогаем стор вовсе.
    // Замена «таким же» списком выдала бы экранам новые id, React пересоздал бы
    // карточки и закрыл открытую модалку редактирования вместе с несохранённой формой.
    if (!screensDiffer(current, serverScreens)) {
      return;
    }

    set((state) => ({
      screensByStory: {
        ...state.screensByStory,
        [storyId]: serverScreens,
      },
      originalScreensByStory: {
        ...state.originalScreensByStory,
        [storyId]: serverScreens,
      },
    }));
  },

  clearStory: (storyId: string) => {
    set((state) => {
      const {[storyId]: removedScreens, ...restScreens} = state.screensByStory;
      const {[storyId]: removedOriginal, ...restOriginal} = state.originalScreensByStory;
      return {
        screensByStory: restScreens,
        originalScreensByStory: restOriginal,
      };
    });
  },

  hasChanges: (storyId: string) => {
    const current = get().screensByStory[storyId] || [];
    const original = get().originalScreensByStory[storyId] || [];

    return screensDiffer(current, original);
  },
}));
